/* import __COLOCATED_TEMPLATE__ from './collections-tree.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
/* === ⚠️ THIS FILE CURRENTLY USES DEPRECATED PATTERNS ⚠️ === */
/* === 🔗 For more information visit https://go.inter.com/ember-best-practices 🔗 */
/* === 🚀 Please consider refactoring & removing some of the comments below when working on this file 🚀 */
/* eslint-disable @intercom/intercom/no-default-task-ember-concurrency */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { later, schedule } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import { type DropActionType, Tree, TreeItem, type TreeParent } from 'embercom/objects/tree-list';
import { inject as service } from '@ember/service';
import {
  buildNewArticleNode,
  buildNewCollectionNode,
  canDropItem,
  didDropItem,
  sortContent,
  updateCountForCollectionAndAncestors,
  indexForDropInside,
  focusOnItem,
  TIMEOUTS,
  checkMaxLevel,
  MAX_LEVEL,
} from './collection-tree-helper';
import {
  type ArticleSummary,
  type ArticleModel,
  DataTypes,
  type Collection,
  type DataObject,
} from './types';
import {
  CAN_MANAGE_ARTICLES_PERMISSION,
  MAX_ARTICLES_IN_A_COLLECTION,
} from 'embercom/lib/articles/constants';
import type Store from '@ember-data/store';
import type ArticleGroup from 'embercom/models/articles/article-group';
import type EmberObject from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type ArticleMultilingual from 'embercom/models/article-multilingual';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { all } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import type IntercomConfirmService from 'embercom/services/intercom-confirm-service';
import { type AuthorSummary } from '../../collections-editor-and-preview';
import { addEventListener } from 'ember-lifeline';
import type ArticleContent from 'embercom/models/articles/article-content';
import type Admin from 'embercom/models/admin';
import { modifier } from 'ember-modifier';
import type { ViewId } from 'embercom/components/knowledge-hub/setup-modal';
import { type Action } from 'embercom/components/common/hero-banner';

interface Args {
  collections: Array<Collection>;
  articleSummaries: Array<ArticleSummary>;
  locale: string;
  defaultLocale: string;
  selectedCollection?: Collection;
  deleteCollection?: (collection: Collection) => Promise<boolean>;
  registerTree: (tree: any) => void;
  getContainerReference: () => HTMLElement;
  helpCenterId: string;
  onCollectionChange?: (tree: Tree) => void;
  refreshData: () => void;
}

let SCROLL_PARAMS = {
  step: 7,
  bottomThreshold: 150,
  topThreshold: 150,
};

export default class CollectionsTree extends Component<Args> {
  root = this.args.collections.find((collection: Collection) => collection.get('isHome'));
  @tracked tree = new Tree(
    [],
    {
      canDragItem: (item: TreeItem) => {
        let mainCollections = item.tree.children.map(
          (i) => (i.dataObject as DataObject).content as Collection,
        );
        return (
          mainCollections.every((c) => !c.read_only) &&
          item.dataObject?.type !== DataTypes.placeholder
        );
      },
      canDropItem,
      willDropItem: (item: TreeItem, newParent: TreeParent, _: DropActionType) => {
        let shouldContinue = true;
        let dataObject = item.dataObject as DataObject;
        if (dataObject.type === DataTypes.collection) {
          shouldContinue = checkMaxLevel(newParent, item);
        }
        if (!shouldContinue) {
          this.notificationsService.notifyError(
            this.intl.t('articles.collections.tree.notification.error-max-level', {
              maxLevel: MAX_LEVEL + 1,
            }),
          );
        }
        return shouldContinue;
      },
      didDropItem: (item: TreeItem, oldParent: TreeParent, dropActionTye: DropActionType) =>
        this.root &&
        didDropItem(
          item,
          this.root,
          oldParent,
          dropActionTye,
          this.reorderCollection,
          this.moveCollection,
          this.moveArticle,
        ),
      indexForDropInside,
    },
    {
      insertionPoint: {
        classes: 'w-full rounded transition-all duration-200 ease-in-out rounded',
        activeClasses: 'bg-neutral-container-emphasis border-2 border-dotted border-neutral-border',
      },
      childList: {
        classes: '',
      },
      listItem: {
        classes:
          'flex flex-row items-center rounded border-transparent hover:bg-neutral-container transition-colors duration-200 ease-in-out',
        activeClasses: 'bg-neutral-container-emphasis',
      },
      draggableContainer: {
        classes: 'flex flex-col',
      },
      dragProperties: {
        minDistanceInPX: 20,
        onDrag: (y: number) => {
          let containerReference = this.args.getContainerReference();
          if (containerReference) {
            if (y > window.innerHeight - SCROLL_PARAMS.bottomThreshold) {
              containerReference.scrollTo({
                left: containerReference.scrollLeft,
                top: containerReference.scrollTop + SCROLL_PARAMS.step,
              });
            } else if (y < SCROLL_PARAMS.topThreshold) {
              containerReference.scrollTo({
                left: containerReference.scrollLeft,
                top: containerReference.scrollTop - SCROLL_PARAMS.step,
              });
            }
          }
        },
        shortDraggingImage: true,
      },
    },
  );

  expandedCollectionIds = new Set<string>();
  existingCollectionIds = new Set<string>();
  selectedCollection = this.args.selectedCollection;

  @service notificationsService: any;
  @service declare intl: IntlService;
  @service declare permissionsService: any;
  @service declare store: Store;
  @service declare appService: AppService;
  @service declare intercomConfirmService: IntercomConfirmService;
  @service declare helpCenterService: $TSFixMe;

  @tracked newCollectionParent: ArticleGroup | undefined;
  @tracked onCreateNewCollection: (id: string) => void = () => {};
  @tracked inModalContext = false;
  @tracked entryPoint: ViewId | undefined;
  @tracked showImportFlowModal = false;

  refreshTreeOnChange = modifier(
    (_: Element, [collections]: [Array<Collection>]) => {
      schedule('afterRender', () => this.refreshTree(collections));
    },
    { eager: false },
  );

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.args.registerTree(this.startCreateNewCollectionFlow.bind(this));
    addEventListener(this, window, 'message', this.handleIframeEvent);
  }

  private refreshTree(collections: Array<Collection>) {
    // Get fresh state of valid collections
    this.existingCollectionIds = new Set(collections.map((collection) => collection.id));
    // Get fresh state of current expanded collections
    this.expandedCollectionIds.clear();
    if (this.selectedCollection) {
      this.initializeExpandedCollections();
    } else {
      this._saveExpandedCollections(this.tree);
    }

    // Re-render tree
    this.initializeTree();

    // Clear collections state
    this.expandedCollectionIds.clear();
    this.existingCollectionIds.clear();
    if (this.selectedCollection) {
      this.selectedCollection = undefined;
    }

    // Notify changes to the preview
    this.args.onCollectionChange?.(this.tree);
  }

  private _saveExpandedCollections(item: TreeParent) {
    item.children?.forEach((child: TreeItem<DataObject>) => {
      if (child.dataObject.type === DataTypes.collection && child.isExpanded) {
        this.expandedCollectionIds.add(child.dataObject.content.id);
      }
      this._saveExpandedCollections(child);
    });
  }

  private _addPlaceholderItemsIfNecessary(item: TreeItem) {
    let hasNoArticlesOrCollections =
      item.children?.filter(
        (child) =>
          child.dataObject?.type === DataTypes.article ||
          child.dataObject?.type === DataTypes.collection,
      ).length === 0;
    let hasNoPlaceholderItems =
      item.children?.filter((child) => child.dataObject?.type === DataTypes.placeholder).length ===
      0;
    if (
      item.dataObject?.type === DataTypes.collection &&
      hasNoArticlesOrCollections &&
      hasNoPlaceholderItems
    ) {
      let placeholderItem = this.buildNewPlaceholderItem(
        this.tree,
        item,
        Number(item.dataObject.level) + 1,
      );
      item.addChildAtIndex(placeholderItem, 0);
    }
    item.children?.forEach((child) => this._addPlaceholderItemsIfNecessary(child));
  }

  private _removePlaceholderItemsIfNecessary(item: TreeItem) {
    let hasArticlesOrCollections =
      item.children?.filter(
        (child) =>
          child.dataObject?.type === DataTypes.article ||
          child.dataObject?.type === DataTypes.collection,
      ).length !== 0;
    let hasPlaceholderItems =
      item.children?.filter((child) => child.dataObject?.type === DataTypes.placeholder).length !==
      0;
    if (
      item.dataObject?.type === DataTypes.collection &&
      hasArticlesOrCollections &&
      hasPlaceholderItems
    ) {
      item.children = item.children?.filter(
        (child) => child.dataObject?.type !== DataTypes.placeholder,
      );
    }
    item.children?.forEach((child) => this._removePlaceholderItemsIfNecessary(child));
  }

  private handleIframeEvent(event: MessageEvent) {
    if (event.data === 'help-center-react-preview-ready') {
      this.args.onCollectionChange?.(this.tree);
    }
  }

  get app() {
    return this.appService.app;
  }

  get showCollections() {
    // if siteHasAtLeastOneCollection has not been set yet, default to the collections length
    if (this.helpCenterService.siteHasAtLeastOneCollection === undefined) {
      return this.args.collections.length > 0;
    } else {
      // if siteHasAtLeastOneCollection has been set, use that value. Helps override the logic to show
      // the collections, allowing for super-smooth transitions when deleting the last collection or adding
      // the first collection
      return this.helpCenterService.siteHasAtLeastOneCollection;
    }
  }

  get newHeroBannerActions(): Action[] {
    return [
      {
        type: 'link',
        label: 'new-settings.helpcenter.new-banner.actions.article-set-up-your-help-center',
        icon: 'article',
        analyticsId: 'set-up-your-help-center-article',
        onClick: () => {
          window.Intercom('showArticle', 1970126); // https://www.intercom.com/help/en/articles/1970126-set-up-your-help-center
        },
      },
      {
        type: 'link',
        label: 'new-settings.helpcenter.new-banner.actions.article-help-center-for-fin-ai-agent',
        icon: 'article',
        analyticsId: 'help-center-for-fin-ai-agent-article',
        onClick: () => {
          window.Intercom('showArticle', 7269326); // https://www.intercom.com/help/en/articles/7269326-optimize-your-help-center-for-fin-ai-agent
        },
      },
      {
        type: 'link',
        label: 'new-settings.helpcenter.new-banner.actions.article-add-knowledge-sources',
        icon: 'article',
        analyticsId: 'add-knowledge-sources-article',
        onClick: () => {
          window.Intercom('showArticle', 9440354); // https://www.intercom.com/help/en/articles/9440354-knowledge-sources-to-power-ai-agents-and-self-serve-support
        },
      },
    ];
  }

  @action
  async startCreateNewCollectionFlowWithNoParent(calledFromCollectionsPickerModal: boolean) {
    this.inModalContext = calledFromCollectionsPickerModal;
    return this.startCreateNewCollectionFlow();
  }

  @action
  async startCreateNewCollectionFlow(parentId?: string, parentItem?: TreeItem) {
    this.newCollectionParent = parentId
      ? this.store.peekRecord('articles/article-group', parentId)
      : undefined;
    this.onCreateNewCollection = async (newCollectionId: string) => {
      if (parentId) {
        let parentDataObject = parentItem?.dataObject as DataObject;
        let newChildren = await this.addCollection(newCollectionId, parentDataObject.level + 1);
        parentItem?.addChildren([newChildren]);
        if (parentItem && !parentItem.isExpanded) {
          parentItem.isExpanded = true;
          later(() => focusOnItem(newChildren), TIMEOUTS.expansionAndInsertion);
        }
      } else {
        let newChildren = await this.addRootCollection(newCollectionId);
        later(() => focusOnItem(newChildren), TIMEOUTS.insertion);
      }

      this.entryPoint = undefined;
      this.newCollectionParent = undefined;
      this.onCreateNewCollection = () => {};
    };

    this.entryPoint = 'collection-editor';
  }

  @action
  async addRootCollection(id: string) {
    let newChild = this.store.peekRecord('articles/article-group', id);
    let newRootCollection = this.defaultBuildNewCollectionNode(newChild);
    this.tree.children.addObject(newRootCollection);
    return newRootCollection;
  }

  initializeExpandedCollections() {
    let selectedCollection = this.selectedCollection;
    if (!selectedCollection) {
      return;
    }

    this.expandedCollectionIds.add(selectedCollection.get('id'));

    let currentCollection = selectedCollection.get('parent');
    while (currentCollection && currentCollection.get('id') !== 'home') {
      this.expandedCollectionIds.add(currentCollection.get('id'));
      currentCollection = currentCollection.get('parent') as Collection;
    }
  }

  initializeTree() {
    let collectionIdArticleMap = this.collectionToArticlesMap();

    if (this.root) {
      let rootChildren = this.root.children
        .toArray()
        .filter(
          (child) =>
            this.existingCollectionIds.has(child.id) &&
            child.helpCenterId === this.args.helpCenterId,
        )
        .sort(sortContent);

      this.tree.children = rootChildren.map((child: any) =>
        this.createCollectionTree(child as Collection, this.tree, collectionIdArticleMap, 0),
      );
    }
  }

  private collectionToArticlesMap() {
    return this.args.articleSummaries.reduce((acc, element: ArticleSummary) => {
      element.parent_collections.forEach((collectionId: number) => {
        let collection = String(collectionId);
        let articles = acc.get(collection) || [];
        articles.push(element);
        acc.set(collection, articles);
      });
      return acc;
    }, new Map<string, Array<ArticleSummary>>());
  }

  private defaultBuildNewCollectionNode(newChildren: Collection, level = 0) {
    return buildNewCollectionNode(
      this.tree,
      newChildren,
      level,
      () => this.args.locale,
      this.addCollectionToParent,
      this.addArticlesToCollection,
      this.listAddableArticles,
      this.removeCollection,
      this.saveCollection,
      false,
      true,
    );
  }

  private buildNewPlaceholderItem(tree: Tree, parentNode: TreeItem, level: number) {
    return new TreeItem({
      tree,
      parent: parentNode,
      children: [],
      isExpanded: false,
      canHaveChildren: false,
      dataObject: {
        locale: () => {},
        type: DataTypes.placeholder,
        level,
      },
      component: 'articles/site/collections/tree/article-placeholder-item',
    });
  }

  @action
  async addCollection(id: string, level = 0) {
    let newChild = this.store.peekRecord('articles/article-group', id);
    return this.defaultBuildNewCollectionNode(newChild, level);
  }

  createCollectionTree(
    node: Collection,
    tree: Tree,
    groupIdArticleMap: Map<string, Array<ArticleSummary>>,
    level: number,
  ): TreeItem {
    let articleNodes: Array<TreeItem> = [];
    let articles = groupIdArticleMap.get(node.id);
    if (articles) {
      let articlesInOrder = [...articles];
      articlesInOrder.sort(sortContent);
      articleNodes = articlesInOrder.map((article) => {
        article.collection = node;
        return buildNewArticleNode(
          tree,
          article,
          level + 1,
          () => this.args.locale,
          () => this.args.defaultLocale,
          this.removeArticleFromCollection,
        );
      });
    }
    let collectionsInOrder = node.children
      .toArray()
      .filter((child) => this.existingCollectionIds.has(child.id));
    collectionsInOrder.sort(sortContent);

    let childrenNodes = collectionsInOrder.map((childNode: Collection) =>
      this.createCollectionTree(childNode, tree, groupIdArticleMap, level + 1),
    );
    let totalChildren = articleNodes.length + childrenNodes.length;
    // if the collection has no children it should be expanded by default
    let isExpanded = this.expandedCollectionIds.has(node.id) || totalChildren === 0;
    let parentNode = buildNewCollectionNode(
      tree,
      node,
      level,
      () => this.args.locale,
      this.addCollectionToParent,
      this.addArticlesToCollection,
      this.listAddableArticles,
      this.removeCollection,
      this.saveCollection,
      isExpanded,
    );
    parentNode.addChildren(articleNodes);
    parentNode.addChildren(childrenNodes);
    this._addPlaceholderItemsIfNecessary(parentNode);
    return parentNode;
  }

  @action
  async addCollectionToParent(parentItem: TreeItem) {
    let parentDataObject = parentItem.dataObject as DataObject;
    if (parentDataObject.type === DataTypes.collection) {
      let collection = parentDataObject.content as Collection;
      this.startCreateNewCollectionFlow(collection.id, parentItem);
    }
  }

  @action
  async addArticlesToCollection(articles: Array<ArticleMultilingual>, collectionItem: TreeItem) {
    let collectionDataObject = collectionItem.dataObject as DataObject;
    if (collectionDataObject.type === DataTypes.collection) {
      let collection = collectionDataObject.content as Collection;
      let updatedArticles = await taskFor(this.addArticlesToCollectionTask).perform(
        articles,
        collection,
      );
      if (!updatedArticles) {
        return;
      }
      updatedArticles.sort(sortContent);
      let newArticleNodes: Array<TreeItem> = updatedArticles.map((article: ArticleMultilingual) => {
        let articleSummary = {
          id: article.get('id'),
          public: !article.hasRestrictedAudience,
          collection,
          article_group_id: collection.get('id'),
          order: article.order,
          locales: article.articleContents.reduce((acc: any, element: ArticleContent) => {
            acc[element.locale] = {
              title: element.title,
              summary: element.summary,
              state: element.state,
              author: this.buildAuthorSummary(element.author),
            };
            return acc;
          }, {}),
          read_only: article.readOnly,
          parent_collections: [], // to be addressed next in adding articles to multiple collections from the collections page
        };
        return buildNewArticleNode(
          this.tree,
          articleSummary,
          collectionDataObject.level + 1,
          () => this.args.locale,
          () => this.args.defaultLocale,
          this.removeArticleFromCollection,
        );
      });
      let nbSiglingArticles =
        collectionItem.children?.filter((child) => {
          let dataObject = child.dataObject as DataObject;
          return dataObject.type === DataTypes.article;
        }).length || 0;
      newArticleNodes.forEach((newArticleNode, index) => {
        collectionItem.children?.insertAt(index + nbSiglingArticles, newArticleNode);
        newArticleNode.parent = collectionItem as TreeParent;
      });
      updateCountForCollectionAndAncestors(collection, newArticleNodes.length);
      if (!collectionItem.isExpanded) {
        collectionItem.isExpanded = true;
        later(() => focusOnItem(collectionItem, false), TIMEOUTS.expansionAndInsertion);
      } else {
        later(() => focusOnItem(collectionItem, false), TIMEOUTS.insertion);
      }
      this._removePlaceholderItemsIfNecessary(collectionItem);

      this.args.onCollectionChange?.(this.tree);
    }
  }

  @action
  async removeCollection(collectionItem: TreeItem) {
    let dataObject = collectionItem.dataObject as DataObject;
    if (dataObject.type === DataTypes.collection) {
      let collection = dataObject.content as Collection;
      let nbArticles = collection.get('count');
      let hasSucceded = await this.deleteCollection(collection);
      if (hasSucceded) {
        collectionItem.parent.children?.removeObject(collectionItem);
      }
      updateCountForCollectionAndAncestors(collection.get('parent'), -nbArticles);
      this._addPlaceholderItemsIfNecessary(collectionItem.parent as TreeItem);
      this.args.onCollectionChange?.(this.tree);
    }
  }

  @action
  async removeArticleFromCollection(articleItem: TreeItem) {
    let dataObject = articleItem.dataObject as DataObject;
    if (dataObject.type !== DataTypes.article) {
      return;
    }

    let article = dataObject.content as ArticleSummary;
    let currentCollection = article.collection;
    let isRemoved = await this.removeArticleFromCollectionMultiple(article, currentCollection);
    if (isRemoved) {
      articleItem.removeFromParent();

      while (currentCollection && !currentCollection.get('isHome')) {
        currentCollection.set('count', currentCollection.get('count') - 1);
        currentCollection = currentCollection.get('parent') as Collection;
      }
    }

    this._addPlaceholderItemsIfNecessary(articleItem.parent as TreeItem);

    this.args.onCollectionChange?.(this.tree);
  }

  @action
  async listAddableArticles(): Promise<Array<ArticleMultilingual>> {
    let allArticles = (await this.store.query('article-multilingual', {
      disable_pagination: true,
    })) as unknown as ArticleMultilingual[];

    return allArticles.filter(
      (article: ArticleMultilingual) => !article.helpCenterIds.includes(this.args.helpCenterId),
    );
  }

  @task
  *addArticlesToCollectionTask(
    articles: Array<ArticleMultilingual>,
    collection: Collection,
  ): TaskGenerator<any> {
    try {
      let saveTasks = [];
      for (let article of articles) {
        saveTasks.push(taskFor(this.addArticleToCollection).perform(article, collection));
      }
      return yield all(saveTasks);
    } catch (err) {
      if (err.jqXHR?.responseJSON?.key === 'max_collection_articles_reached') {
        this.notificationsService.notifyError(
          this.intl.t('articles.collections.add_article.max_articles_reached', {
            limit: MAX_ARTICLES_IN_A_COLLECTION,
          }),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('articles.collections.add_article.error'),
        );
      }
      return false;
    }
  }

  @task({ enqueue: true, maxConcurrency: 1 })
  *addArticleToCollection(
    article: ArticleMultilingual,
    collection: Collection,
  ): TaskGenerator<ArticleMultilingual> {
    article.setProperties({
      folder: collection,
      order: null,
    });
    return yield article.save();
  }

  @action
  async saveCollection(collection: Collection) {
    await this.permissionsService.checkPermission(CAN_MANAGE_ARTICLES_PERMISSION);
    // @ts-ignore
    if (collection.hasDirtyAttributes) {
      await collection.save();
    }
    // Always call to send the updated collection to the preview
    // Updates to icons are not marked as dirty, so we always send info to the preview
    this.args.onCollectionChange?.(this.tree);
  }

  @action
  async deleteCollection(collection: Collection): Promise<boolean> {
    // Needed for testing
    if (this.args.deleteCollection && typeof this.args.deleteCollection === 'function') {
      return this.args.deleteCollection(collection);
    }

    let remainingChildrenBeforeDeletion = this.tree.children;
    await this.permissionsService.checkPermission(CAN_MANAGE_ARTICLES_PERMISSION);
    let confirmOptions = {
      title: this.intl.t('articles.collections.confirm_delete.collection.title'),
      confirmButtonText: this.intl.t(
        'articles.collections.confirm_delete.collection.confirm_button',
      ),
      body:
        collection.count > 0
          ? this.intl.t('articles.collections.confirm_delete.collection.body_with_article')
          : this.intl.t('articles.collections.confirm_delete.collection.body_without_article'),
      cancelButtonText: this.intl.t('articles.collections.confirm_delete.cancel'),
      primaryButtonType: 'primary-destructive',
    };
    let isConfirmed = await this.intercomConfirmService.confirm(confirmOptions);
    if (!isConfirmed) {
      return false;
    }
    collection.destroyRecord();

    if (remainingChildrenBeforeDeletion.length === 1) {
      this.helpCenterService.overrideSiteHasAtLeastOneCollection(false);
    }
    return true;
  }

  @action
  async moveCollection(
    collection: Collection,
    parentCollection: Collection,
    newPosition: number,
    positionMap: any,
  ) {
    await this.permissionsService.checkPermission(CAN_MANAGE_ARTICLES_PERMISSION);
    let oldParentCollectionId = collection.parent?.get('id');
    collection.parent = parentCollection;
    if (!parentCollection.isHome) {
      collection.icon = 'folder';
    }

    this.tree.children?.forEach((child) => {
      let collectionItemAdded =
        oldParentCollectionId && this.findTreeItemById(child, oldParentCollectionId);
      if (collectionItemAdded) {
        this._addPlaceholderItemsIfNecessary(collectionItemAdded);
        this._removePlaceholderItemsIfNecessary(collectionItemAdded);
      }
      let collectionItemRemoved = this.findTreeItemById(child, parentCollection.get('id'));
      if (collectionItemRemoved) {
        this._addPlaceholderItemsIfNecessary(collectionItemRemoved);
        this._removePlaceholderItemsIfNecessary(collectionItemRemoved);
      }
    });
    await collection.save();
    await collection.reorder(newPosition, Object.fromEntries(positionMap), this.args.helpCenterId);
    this.args.onCollectionChange?.(this.tree);
    return collection;
  }

  @action
  async reorderCollection(collection: Collection, newPosition: number, positionMap: any) {
    await this.permissionsService.checkPermission(CAN_MANAGE_ARTICLES_PERMISSION);
    await collection.reorder(newPosition, Object.fromEntries(positionMap), this.args.helpCenterId);
    this.args.onCollectionChange?.(this.tree);
    return collection;
  }

  get confirmOptions() {
    return {
      title: this.intl.t('articles.collections.confirm_delete.article.title'),
      confirmButtonText: this.intl.t('articles.collections.confirm_delete.article.confirm_button'),
      body: this.intl.t('articles.collections.confirm_delete.article.body'),
      primaryButtonType: 'primary-destructive',
    };
  }

  get setupContext() {
    if (this.entryPoint === 'collection-picker') {
      return {
        onClickStartFromScratch: () => {
          this.startCreateNewCollectionFlowWithNoParent(true);
        },
        helpCenterId: this.args.helpCenterId,
        addRootCollection: this.addRootCollection,
        refreshData: this.args.refreshData,
        eventSource: 'collections-page',
      };
    } else if (this.entryPoint === 'collection-editor') {
      return {
        localeId: this.args.locale,
        onCreate: this.onCreateNewCollection,
        selectedHc: this.args.helpCenterId,
        parentCollection: this.newCollectionParent,
        inModalContext: this.inModalContext,
        refreshData: this.args.refreshData,
        eventSource: 'collections-page',
      };
    }
    return {};
  }

  @action
  async removeArticleFromCollectionMultiple(
    articleSummary: ArticleSummary,
    currentCollection: Collection,
  ) {
    await this.permissionsService.checkPermission(CAN_MANAGE_ARTICLES_PERMISSION);

    try {
      let isConfirmed = await this.intercomConfirmService.confirm(this.confirmOptions);

      if (!isConfirmed) {
        return false;
      }

      let article = await this.store.findRecord('article-multilingual', articleSummary.id);
      let oldCollections = await article.inCollections; // await needed as we may need to fetch collections from other help centers
      let newCollections = oldCollections.filter(
        (collection: ArticleGroup) => collection.id !== currentCollection.id,
      );

      article.set('inCollections', newCollections);
      let home = await this.store.findRecord('articles/article-group', 'home');
      let newFolder = newCollections.length > 0 ? newCollections.firstObject : home;
      article.set('folder', newFolder);

      await article.get('updateArticlesMultiple').perform();

      return true;
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('articles.collections.remove_article.error'),
      );
      return false;
    }
  }

  findTreeItemById(tree: TreeItem, id: string): TreeItem | undefined {
    if (tree.dataObject?.content?.id === id) {
      return tree;
    }
    return tree.children?.reduce(
      (acc, child) => this.findTreeItemById(child, id) || acc,
      undefined,
    );
  }

  @action
  async moveArticle(
    articleSummary: ArticleModel,
    collection: Collection,
    order: number,
    oldCollection: Collection,
  ) {
    await this.permissionsService.checkPermission(CAN_MANAGE_ARTICLES_PERMISSION);

    let article: EmberObject & ArticleModel = await this.store.findRecord(
      'article-multilingual',
      articleSummary.id,
    );
    if (oldCollection) {
      article.set('folder', collection);
      await article.save();
    }
    await article.reorder(order, this.args.helpCenterId);
    this.tree.children?.forEach((child) => {
      let collectionItemAdded = this.findTreeItemById(child, oldCollection.id);
      if (collectionItemAdded) {
        this._addPlaceholderItemsIfNecessary(collectionItemAdded);
      }
      let collectionItemRemoved = this.findTreeItemById(child, collection.id);
      if (collectionItemRemoved) {
        this._removePlaceholderItemsIfNecessary(collectionItemRemoved);
      }
    });

    this.args.onCollectionChange?.(this.tree);
  }

  buildAuthorSummary(author: Admin): AuthorSummary {
    return {
      id: author.id,
      name: author.name,
      first_name: author.firstName,
      avatar_url: author.avatarUrl,
      avatar_shape: 'circle',
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Articles::Site::Collections::Tree::CollectionsTree': typeof CollectionsTree;
    'articles/site/collections/tree/collections-tree': typeof CollectionsTree;
  }
}
